import { computed, Ref, ref } from '@vue/composition-api';
import {
	fetchGroupInvitationCampaigns,
	createGroupInvitationCampaign,
	updateGroupInvitationCampaign,
	updateCampaignInvite,
} from '@front-app/services/GroupInvitationCampaignApi/GroupsInvitationApi';
import {
	GroupInvitationCampaign,
	CreateInvitationCampaignRequest,
	UpdateInvitationCampaignRequest,
	InvitationInviteType,
	UpsertInvitationCampaignInvite,
} from '@front-app/services/GroupInvitationCampaignApi/model';
import { useCache } from '@front-app/store/util/cache';
import { getGlobalContext } from '@front-app/util/env';

export interface InvitationCampaignsStore {
	fetchInvitationCampaigns(groupId: string): Promise<void>;
	invitationCampaignById(id: string): GroupInvitationCampaign | undefined;
	groupInvitationCampaigns: Ref<GroupInvitationCampaign[]>;
	createInvitationCampaign(groupId: string, campaign: CreateInvitationCampaignRequest): Promise<GroupInvitationCampaign>;
	updateInvitationCampaign(
		groupId: string,
		campaignId: string,
		campaign: UpdateInvitationCampaignRequest,
		): Promise<void>;
	updateInvitationCampaignInvite(
		groupId: string,
		campaignId: string,
		type: InvitationInviteType,
		updatedInvite: UpsertInvitationCampaignInvite,
	): Promise<void>;
	updateLocalInvitationCampaign(id: string, updates: Partial<GroupInvitationCampaign>): void;
}

/* Prevent refetching invite campaigns until 5 minutes have passed */
const TIMEOUT = 1000 * 60 * 5;

export const getGroupInvitationCampaignsStore = (): InvitationCampaignsStore => {
	const groupInvitationCampaignsCache = useCache({}, getEmptyInvitationCampaign);
	let pend: Promise<void> = Promise.resolve();
	let tid: undefined | number;
	const campaignsForGroup = ref([]) as Ref<string[]>;

	const fetchInvitationCampaigns = async (groupId: string) => {
		pend = Promise.resolve();
		getGlobalContext().clearTimeout(tid);
		tid = getGlobalContext().setTimeout(() => {
			pend = Promise.resolve();
		}, TIMEOUT);
		pend = fetchGroupInvitationCampaigns(groupId)
			.then((invitationCampaigns) => {
				invitationCampaigns.forEach((invitationCampaign) => {
					groupInvitationCampaignsCache.set(invitationCampaign.id, invitationCampaign);
				});
				campaignsForGroup.value = invitationCampaigns.map(({ id }) => id);
			})
			.catch((e) => {
				// eslint-disable-next-line no-console
				console.error('Error fetching or parsing invite campaigns', e);
			});
		return pend;
	};

	const invitationCampaignById = (id: string) => groupInvitationCampaignsCache.get(id);

	const groupInvitationCampaigns = computed(() => campaignsForGroup.value.map(invitationCampaignById));

	const createInvitationCampaign = async (groupId: string, campaign: CreateInvitationCampaignRequest) => {
		const newCampaign = await createGroupInvitationCampaign(groupId, campaign);
		groupInvitationCampaignsCache.set(newCampaign.id, newCampaign);
		campaignsForGroup.value = [...campaignsForGroup.value, newCampaign.id];

		return invitationCampaignById(newCampaign.id);
	};

	const updateInvitationCampaign = async (
		groupId: string,
		campaignId: string,
		campaign: UpdateInvitationCampaignRequest,
	) => {
		const updatedCampaign = await updateGroupInvitationCampaign(groupId, campaignId, campaign);
		groupInvitationCampaignsCache.update(campaignId, updatedCampaign);
	};

	const updateInvitationCampaignInvite = async (
		groupId: string,
		campaignId: string,
		type: InvitationInviteType,
		updatedInvite: UpsertInvitationCampaignInvite,
	) => {
		const updatedCampaignInvite = await updateCampaignInvite(groupId, campaignId, type, updatedInvite);
		const campaignToUpdate = invitationCampaignById(campaignId);
		campaignToUpdate.invites[type] = updatedCampaignInvite;
		groupInvitationCampaignsCache.update(campaignId, campaignToUpdate);
	};

	return {
		fetchInvitationCampaigns,
		invitationCampaignById,
		groupInvitationCampaigns,
		createInvitationCampaign,
		updateInvitationCampaign,
		updateInvitationCampaignInvite,
		updateLocalInvitationCampaign: groupInvitationCampaignsCache.update,
	};
};

function getEmptyInvitationCampaign(id: string): GroupInvitationCampaign {
	return {
		id,
		name: '',
		lastModifiedDateTime: '',
		reminderSendHour: 0,
		invites: {
			initial: {
				version: 0,
				type: InvitationInviteType.INITIAL,
				lastModifiedDateTime: '',
				subject: '',
				topHtml: '',
				bottomHtml: '',
			},
			reminder: {
				version: 0,
				type: InvitationInviteType.REMINDER,
				lastModifiedDateTime: '',
				subject: '',
				topHtml: '',
				bottomHtml: '',
			},
		},
	};
}
